import { useEffect, useState, useCallback, useRef } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Download, Search, Calendar as CalendarIcon, X } from "lucide-react";
import * as XLSX from "xlsx";
import {
  get_order_biller,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";
import { useToast } from "../components/ui/use-toast";
import Order from "./Order.js";
import AdvanceOrder from "./AdvanceOrder.js";

// local yyyy-mm-dd (toISOString IST me ek din peeche kar deta hai)
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function OrderBilling() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("order");
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [orderDatas, setOrderData] = useState([]);
  const [advanceOrderDatas, setAdvanceOrderData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOrderData, setFilteredOrderData] = useState([]);
  const [filteredAdvanceOrderData, setFilteredAdvanceOrderData] = useState([]);

  // Date filter states -> DEFAULT today
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  const didInit = useRef(false);

  // ---- Backend ko bhejne ke liye yyyy-mm-dd range ----
  const getDateRange = useCallback((filter, fromDate = "", toDate = "") => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filter) {
      case "today":
        return { from: fmt(today), to: fmt(today) };

      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: fmt(yesterday), to: fmt(yesterday) };
      }

      case "last-week": {
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 6);
        return { from: fmt(lastWeek), to: fmt(today) };
      }

      case "last-month": {
        const lastMonth = new Date(today);
        lastMonth.setDate(lastMonth.getDate() - 29);
        return { from: fmt(lastMonth), to: fmt(today) };
      }

      case "last-year": {
        const lastYear = new Date(today);
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        return { from: fmt(lastYear), to: fmt(today) };
      }

      case "custom":
        if (fromDate && toDate) return { from: fromDate, to: toDate };
        return null;

      default: // "all"
        return null;
    }
  }, []);

  // ---- Advance orders ke liye client-side date filter
  // (backend advance_order pe entry_date filter nahi lagata) ----
  const filterAdvanceByDate = useCallback((orders, range) => {
    if (!range) return orders;

    const from = new Date(`${range.from}T00:00:00`);
    const to = new Date(`${range.to}T23:59:59.999`);

    return orders.filter((order) => {
      const raw =
        order.entry_date ||
        order.created_at ||
        order.order_date ||
        order.date ||
        order.createdAt;

      if (!raw) return true;

      const orderDate = new Date(raw);
      if (isNaN(orderDate.getTime())) return true;

      return orderDate >= from && orderDate <= to;
    });
  }, []);

  // ---- Search + advance date filter ek jagah ----
  const applyClientFilters = useCallback(
    (orders, advanceOrders, search, range) => {
      let filteredOrders = [...(orders || [])];
      // advance pe pehle date filter
      let filteredAdvance = filterAdvanceByDate([...(advanceOrders || [])], range);

      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredOrders = filteredOrders.filter(
          (order) =>
            order.guest_name?.toLowerCase().includes(searchLower) ||
            order.primary_id?.toString().includes(search)
        );
        filteredAdvance = filteredAdvance.filter(
          (order) =>
            order.guest_name?.toLowerCase().includes(searchLower) ||
            order.primary_id?.toString().includes(search)
        );
      }

      setFilteredOrderData(filteredOrders);
      setFilteredAdvanceOrderData(filteredAdvance);
    },
    [filterAdvanceByDate]
  );

  const getTotalFinalAmount = useCallback((orders) => {
    if (!orders || !Array.isArray(orders)) return 0;
    return orders.reduce(
      (total, order) => total + (parseFloat(order.final_amount) || 0),
      0
    );
  }, []);

  const totalFinalAmount = getTotalFinalAmount(
    activeTab === "order" ? filteredOrderData : filteredAdvanceOrderData
  );

  // ---- Backend call: start_date / end_date ----
  const master_data_get = useCallback(
    async (filter = "today", fromDate = "", toDate = "") => {
      setShowLoaderAdmin(true);

      const range = getDateRange(filter, fromDate, toDate);

      const fd = new FormData();
      fd.append("start_date", range ? range.from : "");
      fd.append("end_date", range ? range.to : "");

      try {
        const response = await server_post_data(get_order_biller, fd);

        if (!response.data.error) {
          const orderData = response.data.message.data_order || [];
          const advanceData = response.data.message.advance_order || [];

          setOrderData(orderData);
          setAdvanceOrderData(advanceData);
          applyClientFilters(orderData, advanceData, searchQuery, range);
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching order data:", error);
        toast({
          title: "Error",
          description: "Failed to load order data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [getDateRange, applyClientFilters, searchQuery, toast]
  );

  // ---- Search (client side) ----
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const range = getDateRange(dateFilter, customDateFrom, customDateTo);
      applyClientFilters(orderDatas, advanceOrderDatas, query, range);
    },
    [
      orderDatas,
      advanceOrderDatas,
      dateFilter,
      customDateFrom,
      customDateTo,
      getDateRange,
      applyClientFilters,
    ]
  );

  // ---- Date filter change -> backend hit ----
  const handleDateFilterChange = useCallback(
    (value) => {
      setDateFilter(value);
      if (value === "custom") {
        setShowCustomDatePicker(true);
      } else {
        setShowCustomDatePicker(false);
        master_data_get(value, "", "");
      }
    },
    [master_data_get]
  );

  // ---- Custom range apply -> backend hit ----
  const handleApplyCustomDate = useCallback(() => {
    if (!customDateFrom || !customDateTo) {
      toast({
        title: "Invalid Date Range",
        description: "Please select both start and end dates",
        variant: "destructive",
      });
      return;
    }

    if (new Date(customDateFrom) > new Date(customDateTo)) {
      toast({
        title: "Invalid Date Range",
        description: "Start date must be before end date",
        variant: "destructive",
      });
      return;
    }

    master_data_get("custom", customDateFrom, customDateTo);
    setShowCustomDatePicker(false);
  }, [customDateFrom, customDateTo, master_data_get, toast]);

  // ---- Clear -> wapas today ----
  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setDateFilter("today");
    setCustomDateFrom("");
    setCustomDateTo("");
    setShowCustomDatePicker(false);
    master_data_get("today", "", "");
  }, [master_data_get]);

  // ---- Excel ----
  const downloadExcel = useCallback(
    (orderData = [], advanceOrderData = [], filename = "Orders.xlsx") => {
      if (!orderData.length && !advanceOrderData.length) {
        toast({
          title: "No Data",
          description: "No data available for export!",
          variant: "destructive",
        });
        return;
      }

      const formattedOrderData = orderData.map((item) => ({
        ...item,
        details: JSON.stringify(item.details || []),
        kot: JSON.stringify(item.kot || []),
        kotdetails: JSON.stringify(item.kotdetails || []),
      }));

      const formattedAdvanceOrderData = advanceOrderData.map((item) => ({
        ...item,
        advance_order: JSON.stringify(item.advance_order || []),
        data_order: JSON.stringify(item.data_order || []),
      }));

      const orderSheet = XLSX.utils.json_to_sheet(formattedOrderData);
      const advanceOrderSheet = XLSX.utils.json_to_sheet(
        formattedAdvanceOrderData
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, orderSheet, "Orders");
      XLSX.utils.book_append_sheet(
        workbook,
        advanceOrderSheet,
        "Advance Orders"
      );

      XLSX.writeFile(workbook, filename);

      toast({
        title: "Success",
        description: "Excel file downloaded successfully!",
      });
    },
    [toast]
  );

  // ---- Initial load: TODAY ----
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    master_data_get("today", "", "");
  }, []);

  const stats = {
    totalOrders: filteredOrderData.length,
    totalAdvanceOrders: filteredAdvanceOrderData.length,
    totalAmount: getTotalFinalAmount(filteredOrderData),
    totalAdvanceAmount: getTotalFinalAmount(filteredAdvanceOrderData),
  };

  const getDateRangeLabel = () => {
    if (dateFilter === "all") return "All Time";
    if (dateFilter === "today") return "Today";
    if (dateFilter === "yesterday") return "Yesterday";
    if (dateFilter === "last-week") return "Last 7 Days";
    if (dateFilter === "last-month") return "Last 30 Days";
    if (dateFilter === "last-year") return "Last Year";
    if (dateFilter === "custom" && customDateFrom && customDateTo) {
      return `${new Date(customDateFrom).toLocaleDateString()} - ${new Date(
        customDateTo
      ).toLocaleDateString()}`;
    }
    return "Select Date Range";
  };

  return (
    <MainLayout>
      <Header
        title="Order & Billing"
        subtitle="Manage orders and advance orders efficiently"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalOrders}
                </p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === "order" ? "Filtered Orders" : "Total Orders"}
                </p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalAdvanceOrders}
                </p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === "advance"
                    ? "Filtered Advance"
                    : "Advance Orders"}
                </p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{totalFinalAmount.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Total Amount</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab("order")}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === "order"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Order ({stats.totalOrders})
          </button>
          <button
            onClick={() => setActiveTab("advance")}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === "advance"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Advance Order ({stats.totalAdvanceOrders})
          </button>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or order number..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Date Filter and Actions */}
            <div className="flex gap-3">
              <Select value={dateFilter} onValueChange={handleDateFilterChange}>
                <SelectTrigger className="w-[200px]">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    <SelectValue placeholder="Select date range" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last-week">Last 7 Days</SelectItem>
                  <SelectItem value="last-month">Last 30 Days</SelectItem>
                  <SelectItem value="last-year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters Button */}
              {(searchQuery || dateFilter !== "today") && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleClearFilters}
                  title="Clear all filters"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}

              {/* Export Button */}
              <Button
                onClick={() =>
                  downloadExcel(
                    activeTab === "order"
                      ? filteredOrderData
                      : filteredAdvanceOrderData,
                    [],
                    `${activeTab}_${getDateRangeLabel()}_${fmt(
                      new Date()
                    )}.xlsx`
                  )
                }
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <div className="flex items-end gap-3 p-4 bg-muted/30 rounded-lg border">
              <div className="flex-1 space-y-2">
                <Label htmlFor="dateFrom">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  max={customDateTo || fmt(new Date())}
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="dateTo">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  min={customDateFrom}
                  max={fmt(new Date())}
                />
              </div>
              <Button
                onClick={handleApplyCustomDate}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                Apply
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setDateFilter("today");
                  setCustomDateFrom("");
                  setCustomDateTo("");
                  master_data_get("today", "", "");
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Active Filters Display */}
          {(searchQuery || dateFilter !== "today") && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Active Filters:</span>
              {searchQuery && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded">
                  Search: "{searchQuery}"
                </span>
              )}
              {dateFilter !== "today" && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded">
                  Date: {getDateRangeLabel()}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === "order" && (
          <Order orderDatas={filteredOrderData} isLoading={showLoaderAdmin} />
        )}
        {activeTab === "advance" && (
          <AdvanceOrder
            orderDatas={filteredAdvanceOrderData}
            isLoading={showLoaderAdmin}
          />
        )}
      </div>
    </MainLayout>
  );
}
